// scenes/fournisseurs/FournisseurForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { fournisseurService } from 'services/fournisseurService';

const FournisseurForm = ({ open, onClose, onSuccess, fournisseur, isEditing }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // États du formulaire
  const [formData, setFormData] = useState({
    nom: '',
    telephone: '',
    email: '',
    adresse: '',
    ville: '',
    contact_personne: '',
    notes: '',
    est_actif: true,
  });

  // Réinitialiser le formulaire
  useEffect(() => {
    if (open) {
      if (isEditing && fournisseur) {
        setFormData({
          nom: fournisseur.nom || '',
          telephone: fournisseur.telephone || '',
          email: fournisseur.email || '',
          adresse: fournisseur.adresse || '',
          ville: fournisseur.ville || '',
          contact_personne: fournisseur.contact_personne || '',
          notes: fournisseur.notes || '',
          est_actif: fournisseur.est_actif !== undefined ? fournisseur.est_actif : true,
        });
      } else {
        setFormData({
          nom: '',
          telephone: '',
          email: '',
          adresse: '',
          ville: '',
          contact_personne: '',
          notes: '',
          est_actif: true,
        });
      }
      setError('');
    }
  }, [open, isEditing, fournisseur]);

  // Gestion des changements
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Validation du formulaire
  const validateForm = () => {
    if (!formData.nom.trim()) {
      setError('Le nom du fournisseur est obligatoire');
      return false;
    }
    
    if (formData.nom.length > 100) {
      setError('Le nom ne peut pas dépasser 100 caractères');
      return false;
    }
    
    // Validation email optionnel
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Format d\'email invalide');
      return false;
    }
    
    // Validation téléphone optionnel
    if (formData.telephone && !/^[0-9\s\+\-\(\)]{8,20}$/.test(formData.telephone.replace(/\s/g, ''))) {
      setError('Format de téléphone invalide');
      return false;
    }
    
    return true;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const dataToSend = {
        ...formData,
        // Nettoyer les champs vides
        telephone: formData.telephone || '',
        email: formData.email || '',
        adresse: formData.adresse || '',
        ville: formData.ville || '',
        contact_personne: formData.contact_personne || '',
        notes: formData.notes || '',
      };

      if (isEditing) {
        await fournisseurService.updateFournisseur(fournisseur.id, dataToSend);
      } else {
        await fournisseurService.createFournisseur(dataToSend);
      }
      
      onSuccess();
    } catch (err) {
      console.error('Erreur de soumission:', err);
      
      // Gestion des erreurs spécifiques
      if (err.response?.data) {
        const errorData = err.response.data;
        if (errorData.nom) {
          setError(`Nom: ${Array.isArray(errorData.nom) ? errorData.nom.join(', ') : errorData.nom}`);
        } else if (errorData.email) {
          setError(`Email: ${Array.isArray(errorData.email) ? errorData.email.join(', ') : errorData.email}`);
        } else if (errorData.telephone) {
          setError(`Téléphone: ${Array.isArray(errorData.telephone) ? errorData.telephone.join(', ') : errorData.telephone}`);
        } else if (errorData.detail) {
          setError(errorData.detail);
        } else if (errorData.non_field_errors) {
          setError(Array.isArray(errorData.non_field_errors) 
            ? errorData.non_field_errors.join(', ') 
            : errorData.non_field_errors
          );
        } else {
          setError('Une erreur est survenue lors de l\'enregistrement');
        }
      } else {
        setError(err.message || 'Erreur de connexion au serveur');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            {isEditing ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Informations principales */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Informations principales
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom du fournisseur *"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                variant="outlined"
                required
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText="Nom officiel de l'entreprise fournisseur"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Personne de contact"
                name="contact_personne"
                value={formData.contact_personne}
                onChange={handleChange}
                variant="outlined"
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText="Nom de la personne à contacter"
              />
            </Grid>

            {/* Coordonnées */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Coordonnées
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                variant="outlined"
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText="Format: 01 23 45 67 89"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText="Adresse email de contact"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Adresse"
                name="adresse"
                value={formData.adresse}
                onChange={handleChange}
                variant="outlined"
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText="Adresse postale"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ville"
                name="ville"
                value={formData.ville}
                onChange={handleChange}
                variant="outlined"
                disabled={loading}
                helperText="Ville"
              />
            </Grid>

            {/* Informations complémentaires */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                Informations complémentaires
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={3}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DescriptionIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText="Informations supplémentaires sur le fournisseur"
              />
            </Grid>

            {/* Statut */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.est_actif}
                    onChange={handleSwitchChange}
                    name="est_actif"
                    color="primary"
                    disabled={loading}
                  />
                }
                label={
                  <Box>
                    <Typography>Fournisseur actif</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Désactivez pour masquer ce fournisseur des listes
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>

          <Box mt={3}>
            <Typography variant="caption" color="textSecondary">
              * Champs obligatoires
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {loading ? 'Enregistrement...' : isEditing ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FournisseurForm;